import { useEffect } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { checkFeatureFlagGate } from '@wac-web/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@wac-web/utils/feature-flags/feature-flag-manifest';

import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import * as StateActions from '@/state/actions';

// This hook is called in PageLayout to set up state for all domain-scoped routes
export const useLoadDomainScopedState = (customerDomainId: string | null): void => {
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const { data: user } = useFastshiftSelector((state) => state.userAccess);
	const customerDomain = useFastshiftSelector((state) => state.customerDomain);
	const isOrganizationRedesignEnabled = checkFeatureFlagGate(
		featureFlags.sxpFspOrganizationRedesign,
	);

	useEffect(() => {
		// Fetch is not relevant to non-domain scoped pages
		if (!customerDomainId) return;

		if (!user?.hasMoveTicketAccess) {
			return;
		}

		// Don't refetch if domain-scoped state is already available
		if (customerDomain?.data?.customerDomainId === customerDomainId) return;

		// Clear any outdated domain-scoped data
		// For example - a partner selected a different customer domain
		if (customerDomain?.data) {
			dispatch(StateActions.clearAllCustomerDomainScopedState());
		}

		dispatch(
			StateActions.getCustomerDomain({
				stargateClient,
				customerDomainId,
			}),
		);
		dispatch(
			StateActions.getCustomerDomainEngagementDetails({
				stargateClient,
				customerDomainId,
			}),
		);
		if (isOrganizationRedesignEnabled) {
			dispatch(
				StateActions.getFastshiftOrganizations({
					stargateClient,
					domainId: customerDomainId,
				}),
			);
		}

		// Add once available: retrieve domain-scoped move tickets
	}, [
		customerDomainId,
		dispatch,
		stargateClient,
		user?.hasMoveTicketAccess,
		isOrganizationRedesignEnabled,
	]);
};
